import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import {
  getLoggedInUser,
  isSuperAdminUser,
} from "src/app/shared/utils/plan.util";
import { OrgContextService } from "src/app/Services/org-context.service";

export type OrgAccessArea = "setup" | "admin";

/** Gates org setup / org admin routes; set `data: { orgAccess: "setup" | "admin" }`. */
@Injectable({ providedIn: "root" })
export class OrgAccessGuard implements CanActivate {
  constructor(
    private router: Router,
    private toastr: ToastrService,
    private orgContextService: OrgContextService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> {
    const user = getLoggedInUser();
    if (!user) return this.router.parseUrl("/");
    if (isSuperAdminUser(user)) return true;

    const area: OrgAccessArea =
      route.data?.["orgAccess"] === "setup" ? "setup" : "admin";

    if (this.orgContextService.snapshot.loaded) {
      return this.decide(area, user);
    }
    return this.orgContextService.load().pipe(
      map(() => this.decide(area, user)),
      catchError(() => of(this.decide(area, user)))
    );
  }

  private decide(area: OrgAccessArea, user: any): boolean | UrlTree {
    const allowed =
      area === "setup"
        ? this.orgContextService.canAccessOrgSetup(user)
        : this.orgContextService.canAccessOrgAdminConsole();
    if (allowed) return true;
    this.toastr.info(
      area === "setup"
        ? "Your organization role does not allow organization setup."
        : "Your organization role does not allow access to org admin."
    );
    return this.router.parseUrl("/");
  }
}
